import { GitBranch } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockTable, MockButton, StatusBadge } from "../shared";

export default function AdminVersionsSection() {
  return (
    <div data-testid="guide-admin-versions">
      <PageHeading
        icon={GitBranch}
        title="Versions (Governance)"
        description="Every change to lanes, actors, reference lists, workflow rules, and field permissions happens on a draft copy of the rules. When you turn the draft on, it becomes the live rule set and the old one becomes a saved snapshot."
      />

      <Callout variant="info" title="In plain words">
        Think of this like saving a draft document before you publish it. You never edit the live rules directly. Instead you make a draft copy, change it safely, and only when it's ready do you click <span className="text-white">Activate</span> to switch it on. The previous rules are kept as history, so nothing is lost.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55">Permission: <span className="text-white">manage_governance</span>.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it whenever you need to change how the system routes or controls work — adding a lane, changing who signs off, editing the dropdown lists, and so on. You always start here by cloning the active version into a new draft.
        </p>
      </Section>

      <Section title="Making a governance change">
        <Diagram
          frameLabel="Admin → Versions"
          legend={[
            <>Click <span className="text-white">Clone Active</span> — it makes a draft copy of every current rule.</>,
            <>The new <span className="text-white">draft</span> row appears, editable, with a name and effective date.</>,
            <>When everything is ready, click <span className="text-white">Activate</span> on the draft to switch it on.</>,
          ]}
        >
          <MockButton label="Clone Active" marker={1} />
          <MockTable
            columns={["Version", "Effective from", "Status"]}
            rows={[
              ["2026 Q1 — new Compliance lane", "01 Jan 2026", <StatusBadge status="REGISTERED" />],
              ["2026 Q2 draft", "01 Apr 2026", <span className="inline-flex items-center gap-2"><span className="text-amber-300 text-xs">draft</span><MockButton label="Activate" marker={3} variant="ghost" /></span>],
            ]}
          />
          <div className="text-2xs text-white/40 flex items-center gap-1.5"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span> the editable draft row</div>
        </Diagram>
        <Steps steps={[
          <>Open Versions → <span className="text-white">Clone Active</span>. A new draft is created with every lane / actor / list / rule copied from the current active version.</>,
          <>Give the draft a clear name and the effective-from date.</>,
          <>Edit lanes, actors, reference lists, workflow rules, field permissions, or transition permissions. All edits flow into this draft only — the active version is untouched.</>,
          <>When ready, return to Versions and click <span className="text-white">Activate</span> on the draft. The old active becomes archived; the new draft becomes active immediately.</>,
        ]} />
      </Section>

      <Section title="Version fields">
        <div className="space-y-2">
          <Field name="Name" required="required"><p>Free-text label, e.g. "2026 Q1 — added Compliance lane".</p></Field>
          <Field name="Effective from" required="required"><p>Date the version becomes the active configuration once activated.</p></Field>
          <Field name="Status" required="auto"><p><span className="text-white">draft</span> (editable) → <span className="text-white">active</span> (read-only, currently in force) → eventually <span className="text-white">archived</span> when superseded.</p></Field>
          <Field name="Created by" required="auto"><p>Username of the super-admin who cloned this draft.</p></Field>
        </div>
      </Section>

      <Callout variant="warning" title="Activation is non-reversible from the UI">
        Once a draft is activated, the previous active version is locked into history. Roll back by cloning the historical version into a new draft and activating that. The transition is audit-logged either way.
      </Callout>
      <Callout variant="info" title="Open action items keep their version">
        An action item is stamped with the governance version active at registration time. Activating a new version does not retroactively change the rules an open item plays by. New items registered after activation use the new version.
      </Callout>
    </div>
  );
}
